import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Megaphone, Check } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import { pulsesApi } from "../../lib/pulses";
import { formatApiError } from "../../lib/api";

// NOTE: This is a MOCK dispatch — no real ad-walker service is called.
// The request is only persisted in Mongo and a confirmation is shown.
export function AdWalkerModal() {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const [business, setBusiness] = useState("");
  const [promoText, setPromoText] = useState("");
  const [zone, setZone] = useState("");
  const [duration, setDuration] = useState(4);

  const reset = () => {
    setBusiness("");
    setPromoText("");
    setZone("");
    setDuration(4);
    setDone(null);
  };

  const submit = async (e) => {
    e && e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const data = await pulsesApi.adWalker({
        business: business.trim(),
        promo_text: promoText.trim(),
        zone: zone.trim(),
        duration_hours: Number(duration),
      });
      setDone(data);
      toast.success("Solicitud enviada. Te contactaremos por chat.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) reset();
      }}
    >
      <DialogTrigger asChild>
        <button
          className="w-full q-btn-primary inline-flex items-center justify-center gap-2 !px-6 !py-3 text-sm"
          data-testid="ad-walker-open"
          style={{ boxShadow: "0 0 32px var(--accent-glow)" }}
        >
          <Megaphone size={16} /> Promociona en la calle con Ad-Walker
        </button>
      </DialogTrigger>
      <DialogContent
        className="border-white/10 bg-[#0f172a]/95 backdrop-blur-2xl text-slate-100 max-w-lg"
        data-testid="ad-walker-modal"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-xl">
            Ad-Walker · Promoción en la calle
          </DialogTitle>
          <DialogDescription className="text-slate-400 text-xs">
            Envía una solicitud para que un promotor difunda tu oferta en una zona.
            <span className="block mt-1 q-accent-text font-mono">
              Nota: función piloto (despacho simulado).
            </span>
          </DialogDescription>
        </DialogHeader>

        {done ? (
          <div className="rounded-2xl border border-[color:var(--accent)]/40 bg-[color:var(--accent-soft)] p-4">
            <div
              className="flex items-center gap-2 q-accent-text font-display text-lg"
              data-testid="ad-walker-success"
            >
              <Check size={20} /> Solicitud registrada
            </div>
            <ul className="text-xs text-slate-200 mt-3 space-y-1">
              <li>
                <span className="text-slate-400">Negocio:</span> {done.business}
              </li>
              <li>
                <span className="text-slate-400">Zona:</span> {done.zone}
              </li>
              <li>
                <span className="text-slate-400">Duración:</span> {done.duration_hours} h
              </li>
              <li className="font-mono text-slate-500 mt-2">ID: {done.id}</li>
            </ul>
            <DialogFooter className="mt-4">
              <button
                className="q-btn-primary"
                onClick={() => setOpen(false)}
                type="button"
                data-testid="ad-walker-close"
              >
                Cerrar
              </button>
            </DialogFooter>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3" data-testid="ad-walker-form">
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-slate-400 font-display">
                Nombre del negocio
              </span>
              <input
                className="q-input mt-1"
                data-testid="ad-walker-business"
                value={business}
                onChange={(e) => setBusiness(e.target.value)}
                required
                maxLength={80}
              />
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-slate-400 font-display">
                Texto de la promo
              </span>
              <textarea
                className="q-input mt-1 min-h-[72px] resize-none"
                data-testid="ad-walker-promo"
                value={promoText}
                onChange={(e) => setPromoText(e.target.value)}
                required
                maxLength={200}
              />
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="text-xs uppercase tracking-widest text-slate-400 font-display">
                  Zona (texto libre)
                </span>
                <input
                  className="q-input mt-1"
                  data-testid="ad-walker-zone"
                  value={zone}
                  onChange={(e) => setZone(e.target.value)}
                  required
                  placeholder="Ej. Centro, Roma Norte…"
                  maxLength={80}
                />
              </label>
              <label className="block">
                <span className="text-xs uppercase tracking-widest text-slate-400 font-display">
                  Duración (h)
                </span>
                <input
                  type="number"
                  min={1}
                  max={24}
                  className="q-input mt-1"
                  data-testid="ad-walker-duration"
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                  required
                />
              </label>
            </div>
            <DialogFooter>
              <button
                type="button"
                className="q-btn-ghost"
                onClick={() => setOpen(false)}
                disabled={busy}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="q-btn-primary inline-flex items-center gap-2"
                disabled={busy}
                data-testid="ad-walker-submit"
              >
                {busy && <Loader2 size={14} className="animate-spin" />}
                Enviar solicitud
              </button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
